import { useState } from "react";
import { Check, X, ExternalLink, Pencil, Trash2, Loader2, Mic } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const field = "w-full rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-base)] px-3 py-2 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent)]";
const lbl = "mb-1 block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]";

function Cover({ src, alt }) {
  return src
    ? <img src={src} alt={alt} className="h-20 w-20 flex-none rounded object-cover" />
    : <div className="flex h-20 w-20 flex-none items-center justify-center rounded border border-dashed border-[var(--border-subtle)] text-[var(--text-muted)]"><Mic size={22} /></div>;
}

function ShowLink({ p, testid }) {
  return (
    <a href={p.url} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex max-w-full items-center gap-1 text-xs font-semibold text-[var(--accent)] underline underline-offset-2 hover:text-[var(--accent-hover)]" data-testid={testid}>
      <ExternalLink size={13} className="flex-none" /><span className="break-all">{p.url_label} → {p.url}</span>
    </a>
  );
}

export function PodcastQueue({ subs, onChange }) {
  const act = async (id, action) => {
    let body = {};
    if (action === "approve") {
      const sub = subs.find((x) => x.id === id);
      const owner = window.prompt("Podcaster's Dial account (email or @username) so they can add episodes. Leave blank to link later.", sub?.owner_hint || "") ?? null;
      if (owner === null) return;
      body = owner.trim() ? { owner_identifier: owner.trim() } : {};
    }
    try {
      const { data } = await api.post(`/admin/podcast-submissions/${id}/${action}`, body);
      toast.success(action === "approve" ? `Approved — podcast is live${data.owner_linked ? " and linked to its podcaster" : body.owner_identifier ? " (no account matched — link later)" : ""}` : "Rejected");
      onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className="mt-6" data-testid="admin-podcast-queue">
      <h3 className="text-xs font-bold uppercase tracking-widest text-[var(--text-secondary)]">Podcast queue ({subs.length})</h3>
      <p className="mt-2 rounded-[var(--card-radius)] border border-[var(--border-accent)] bg-[var(--accent)]/10 px-3 py-2 text-sm font-semibold text-[var(--accent-hover)]" data-testid="admin-podcast-guideline">Podcast Spotlight is for TV/film podcasts only.</p>
      {subs.length === 0 ? (
        <p className="mt-3 text-sm text-[var(--text-secondary)]" data-testid="admin-no-podcast-submissions">No pending podcast submissions.</p>
      ) : (
        <div className="mt-3 space-y-3">
          {subs.map((s) => (
            <div key={s.id} className="rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] p-4" data-testid={`admin-podcast-submission-${s.id}`}>
              <div className="flex items-start gap-4">
                <Cover src={s.cover_art} alt={s.show_name} />
                <div className="min-w-0 flex-1">
                  <p className="font-head text-lg leading-snug text-[var(--text-primary)] break-words">{s.show_name}</p>
                  {s.host && <p className="text-xs text-[var(--text-secondary)] break-words">Host: {s.host}</p>}
                  {s.owner_hint && <p className="text-xs text-[var(--text-secondary)] break-words">Dial account: {s.owner_hint}</p>}
                  <p className="mt-1 whitespace-pre-wrap break-words text-sm text-[var(--text-primary)]">{s.description}</p>
                  <ShowLink p={s} testid={`admin-podcast-url-${s.id}`} />
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button onClick={() => act(s.id, "approve")} className="inline-flex items-center gap-1 rounded-[var(--card-radius)] bg-[var(--accent)] px-3 py-2 text-sm font-bold uppercase text-[var(--accent-contrast)]" data-testid={`admin-podcast-approve-${s.id}`}><Check size={15} /> Approve</button>
                <button onClick={() => act(s.id, "reject")} className="inline-flex items-center gap-1 rounded-[var(--card-radius)] border border-[var(--border-subtle)] px-3 py-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]" data-testid={`admin-podcast-reject-${s.id}`}><X size={15} /> Reject</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PodcastEditForm({ p, onDone }) {
  const [f, setF] = useState({ show_name: p.show_name, host: p.host || "", description: p.description, cover_art: p.cover_art || "", url: p.url, url_label: p.url_label });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    setSaving(true);
    try { await api.put(`/admin/podcasts/${p.id}`, f); toast.success("Podcast updated"); onDone(true); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-3 space-y-3 border-t border-[var(--border-subtle)] pt-3" data-testid={`admin-podcast-edit-form-${p.id}`}>
      <div className="grid gap-3 sm:grid-cols-2">
        <div><label className={lbl}>Show name</label><input className={field} value={f.show_name} onChange={set("show_name")} maxLength={120} data-testid="admin-podcast-edit-name" /></div>
        <div><label className={lbl}>Host</label><input className={field} value={f.host} onChange={set("host")} maxLength={120} /></div>
      </div>
      <div><label className={lbl}>Description ({f.description.length}/300)</label><textarea className={`${field} min-h-[80px]`} value={f.description} maxLength={300} onChange={set("description")} data-testid="admin-podcast-edit-description" /></div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div><label className={lbl}>Show URL</label><input className={field} value={f.url} onChange={set("url")} data-testid="admin-podcast-edit-url" /></div>
        <div><label className={lbl}>Link label</label><input className={field} value={f.url_label} onChange={set("url_label")} maxLength={60} data-testid="admin-podcast-edit-label" /></div>
      </div>
      <div><label className={lbl}>Cover art URL</label><input className={field} value={f.cover_art} onChange={set("cover_art")} /></div>
      <div className="flex gap-2">
        <button onClick={save} disabled={saving || !f.show_name || !f.description || !f.url} className="inline-flex items-center gap-1 rounded-[var(--card-radius)] bg-[var(--accent)] px-4 py-2 text-sm font-bold uppercase text-[var(--accent-contrast)] disabled:opacity-60" data-testid="admin-podcast-edit-save">{saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save</button>
        <button onClick={() => onDone(false)} className="inline-flex items-center gap-1 rounded-[var(--card-radius)] border border-[var(--border-subtle)] px-4 py-2 text-sm text-[var(--text-secondary)]" data-testid="admin-podcast-edit-cancel"><X size={15} /> Cancel</button>
      </div>
    </div>
  );
}

export function EpisodeQueue({ episodes, onChange }) {
  const act = async (id, action) => {
    try { await api.post(`/admin/podcast-episodes/${id}/${action}`); toast.success(action === "approve" ? "Episode approved" : "Episode rejected"); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className="mt-6" data-testid="admin-episode-queue">
      <h3 className="text-xs font-bold uppercase tracking-widest text-[var(--text-secondary)]">Podcast episode queue ({episodes.length})</h3>
      {episodes.length === 0 ? <p className="mt-3 text-sm text-[var(--text-secondary)]" data-testid="admin-no-episodes">No pending episodes.</p> : (
        <div className="mt-3 space-y-3">
          {episodes.map((e) => (
            <div key={e.id} className="rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] p-4" data-testid={`admin-episode-${e.id}`}>
              <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">{e.show_name}</p>
              <p className="mt-0.5 break-words font-semibold text-[var(--text-primary)]">{e.title}</p>
              <a href={e.url} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex max-w-full items-center gap-1 text-xs font-semibold text-[var(--accent)] underline underline-offset-2" data-testid={`admin-episode-url-${e.id}`}><ExternalLink size={13} className="flex-none" /><span className="break-all">{e.url}</span></a>
              <p className="mt-1 text-xs text-[var(--text-secondary)]">About: {(e.titles || []).map((t) => t.title).join(", ")}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button onClick={() => act(e.id, "approve")} className="inline-flex items-center gap-1 rounded-[var(--card-radius)] bg-[var(--accent)] px-3 py-2 text-sm font-bold uppercase text-[var(--accent-contrast)]" data-testid={`admin-episode-approve-${e.id}`}><Check size={15} /> Approve</button>
                <button onClick={() => act(e.id, "reject")} className="inline-flex items-center gap-1 rounded-[var(--card-radius)] border border-[var(--border-subtle)] px-3 py-2 text-sm text-[var(--text-secondary)]" data-testid={`admin-episode-reject-${e.id}`}><X size={15} /> Reject</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function PodcastList({ podcasts, onChange }) {
  const [editing, setEditing] = useState(null);
  const setOwner = async (p) => {
    const v = window.prompt("Link this show to a Dial account (email or @username). Leave blank to unlink.", p.owner?.username ? `@${p.owner.username}` : p.owner?.email || "");
    if (v === null) return;
    try { await api.put(`/admin/podcasts/${p.id}/owner`, { owner_identifier: v.trim() }); toast.success(v.trim() ? "Podcaster linked" : "Podcaster unlinked"); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Remove this podcast from The Dial?")) return;
    try { await api.delete(`/admin/podcasts/${id}`); toast("Removed"); onChange(); } catch { toast.error("Failed"); }
  };
  return (
    <section className="mt-12" data-testid="admin-podcasts">
      <h2 className="font-head text-2xl text-[var(--text-primary)]">Podcasts <span className="text-[var(--text-muted)]">({podcasts.length})</span></h2>
      {podcasts.length === 0 && <p className="mt-3 text-sm text-[var(--text-secondary)]">No live podcasts yet.</p>}
      <div className="mt-4 space-y-2">
        {podcasts.map((p) => (
          <div key={p.id} className="rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] p-3" data-testid={`admin-podcast-${p.id}`}>
            <div className="flex items-start gap-4">
              <Cover src={p.cover_art} alt={p.show_name} />
              <div className="min-w-0 flex-1">
                <p className="break-words font-semibold text-[var(--text-primary)]">{p.show_name}</p>
                {p.host && <p className="text-xs text-[var(--text-secondary)]">{p.host}</p>}
                <ShowLink p={p} testid={`admin-podcast-link-${p.id}`} />
                <p className="mt-1 text-xs text-[var(--text-secondary)]" data-testid={`admin-podcast-owner-${p.id}`}>
                  Podcaster: {p.owner ? <span className="text-[var(--text-primary)]">{p.owner.name || p.owner.email}{p.owner.username ? ` (@${p.owner.username})` : ""}</span> : <span className="text-[var(--text-muted)]">not linked</span>}
                  {" · "}<button onClick={() => setOwner(p)} className="font-semibold text-[var(--accent)] underline underline-offset-2" data-testid={`admin-podcast-set-owner-${p.id}`}>{p.owner ? "Change" : "Link account"}</button>
                </p>
              </div>
              <button onClick={() => setEditing(editing === p.id ? null : p.id)} className="rounded p-2 text-[var(--text-secondary)] hover:text-[var(--accent)]" data-testid={`admin-podcast-edit-${p.id}`}><Pencil size={16} /></button>
              <button onClick={() => del(p.id)} className="rounded p-2 text-[var(--text-secondary)] hover:text-red-500" data-testid={`admin-podcast-delete-${p.id}`}><Trash2 size={16} /></button>
            </div>
            {editing === p.id && <PodcastEditForm p={p} onDone={(saved) => { setEditing(null); if (saved) onChange(); }} />}
          </div>
        ))}
      </div>
    </section>
  );
}
